import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ListChecks, Plus, Search, Sparkles, Wand2, Workflow, X, type LucideIcon } from 'lucide-react'
import { useEffect, useMemo, useState, type ReactNode } from 'react'

import {
  ACTION_CATEGORIES,
  actionsQuery,
  ORIGINS,
  RULE_EVENTS,
  rulesQuery,
  type ActionDef,
  type InteractionRule,
  type Origin,
  type RegistryKind,
  type RoleSpec,
} from '@/api/actions'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Chip } from '@/components/ui/chip'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/components/ui/utils'

import { CreateWithAIDialog } from './CreateWithAIDialog'
import { categoryLabel, eventLabel, ruleTitle } from './describe'
import { CapabilitiesEditor } from './editor/CapabilitiesEditor'
import { NewDocPage } from './editor/NewDocPage'
import { useToggleEnabled } from './hooks'
import { EnabledSwitch, ORIGIN_LABEL, OriginBadge, RoleChips, TypeLegend } from './parts'

/**
 * `/actions` — what puppets can do in a story (**Actions**), how the world answers back
 * (**World rules**) and what each kind of thing is able to do (**What things can do**, the
 * capability map), docs/SKEL-STORY.md R7 + §11.7. The first two tabs are searchable card grids:
 * title and one line, chips for the participants (character / object / scenery / place / spot),
 * where it came from (Built-in, Made by AI, Mine) and a switch that decides whether stories may use
 * it. "Create with AI" teaches the studio a new one from a sentence; "New action" / "New world
 * rule" opens the manual editor on a blank (`?new=action|rule`).
 */

export const ACTIONS_TABS = ['actions', 'rules', 'capabilities'] as const
export type ActionsTab = (typeof ACTIONS_TABS)[number]

export interface ActionsSearchState {
  tab?: ActionsTab
  q?: string
  category?: string
  event?: string
  origin?: Origin
  /** The manual editor on a blank action / world rule. */
  new?: RegistryKind
}

export interface ActionsPageProps extends Omit<ActionsSearchState, 'new'> {
  /** `?new=`: make one by hand. */
  creating?: RegistryKind
  onSearchChange?(next: ActionsSearchState): void
  /** Test seam: how often the create-with-AI dialog polls its job. */
  jobPollMs?: number
  jobStream?: boolean
  /** Test seam: how soon the manual editor checks a draft. */
  validateDelay?: number
}

const TAB_INFO: Record<ActionsTab, { label: string; icon: LucideIcon; kind: RegistryKind | null }> = {
  actions: { label: 'Actions', icon: Wand2, kind: 'action' },
  rules: { label: 'World rules', icon: Workflow, kind: 'rule' },
  capabilities: { label: 'What things can do', icon: ListChecks, kind: null },
}

const GRID = 'grid grid-cols-[repeat(auto-fill,minmax(17rem,1fr))] gap-4'
const SELECT = 'h-11 rounded-xl border border-line bg-paper px-2 text-sm text-ink focus-visible:focus-ring'

export function ActionsPage({ tab = 'actions', q = '', category, event, origin, creating: newKind, onSearchChange, jobPollMs, jobStream, validateDelay }: ActionsPageProps) {
  const [draft, setDraft] = useState(q)
  const [creating, setCreating] = useState<{ kind: RegistryKind; description: string } | null>(null)
  const current = useMemo<ActionsSearchState>(() => ({ tab, q: q || undefined, category, event, origin }), [tab, q, category, event, origin])

  // The box is the source while typing; the URL follows 250 ms later (and wins when it changes).
  useEffect(() => setDraft(q), [q])
  useEffect(() => {
    if (draft === q) return
    const t = setTimeout(() => onSearchChange?.({ ...current, q: draft || undefined }), 250)
    return () => clearTimeout(t)
  }, [draft, q, current, onSearchChange])

  const kind: RegistryKind = TAB_INFO[tab].kind ?? 'action'
  const registryTab = TAB_INFO[tab].kind !== null
  const set = (patch: Partial<ActionsSearchState>) => onSearchChange?.({ ...current, ...patch })

  if (newKind) return <NewDocPage kind={newKind} validateDelay={validateDelay} onCancel={() => onSearchChange?.({ ...current, new: undefined })} />

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-4 py-8" data-testid="actions-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex max-w-2xl flex-col gap-1">
          <h1 className="text-3xl font-semibold tracking-tight text-ink">Actions</h1>
          <p className="text-ink-2">Everything puppets can do in a story — with each other, with things, with the place — and how the world answers back.</p>
        </div>
        {registryTab ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => set({ new: kind })} data-testid="new-by-hand">
              <Plus aria-hidden />
              {kind === 'rule' ? 'New world rule' : 'New action'}
            </Button>
            <Button variant="primary" onClick={() => setCreating({ kind, description: '' })} data-testid="create-with-ai">
              <Sparkles aria-hidden />
              Create with AI
            </Button>
          </div>
        ) : null}
      </header>

      <Tabs value={tab} onValueChange={(v) => set({ tab: v as ActionsTab, category: undefined, event: undefined })}>
        <div className="flex flex-wrap items-center gap-3">
          <TabsList aria-label="Actions, world rules or what things can do">
            {ACTIONS_TABS.map((t) => {
              const Icon = TAB_INFO[t].icon
              return (
                <TabsTrigger key={t} value={t} className="h-9 px-3.5" data-testid={`tab-${t}`}>
                  <Icon className="size-4" aria-hidden />
                  {TAB_INFO[t].label}
                </TabsTrigger>
              )
            })}
          </TabsList>
          <div className="relative min-w-56 flex-1 sm:max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-3" aria-hidden />
            <Input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={tab === 'rules' ? 'Find a rule: water, door, lands…' : tab === 'capabilities' ? 'Find: ball, water, can fly…' : 'Find an action: throw, hug, swim…'}
              aria-label={tab === 'rules' ? 'Search world rules' : tab === 'capabilities' ? 'Search what things can do' : 'Search actions'}
              className="pl-9 pr-9"
            />
            {draft ? (
              <button type="button" onClick={() => setDraft('')} aria-label="Clear search" className="absolute right-2 top-1/2 inline-flex size-7 -translate-y-1/2 items-center justify-center rounded-lg text-ink-3 hover:bg-paper-2 hover:text-ink">
                <X className="size-4" aria-hidden />
              </button>
            ) : null}
          </div>
          {registryTab ? (
          <label className="flex items-center gap-2 text-sm text-ink-2">
            <span>Made by</span>
            <select className={SELECT} value={origin ?? ''} onChange={(e) => set({ origin: (e.target.value || undefined) as Origin | undefined })} aria-label="Made by">
              <option value="">Anyone</option>
              {ORIGINS.map((o) => (
                <option key={o} value={o}>
                  {ORIGIN_LABEL[o]}
                </option>
              ))}
            </select>
          </label>
          ) : null}
        </div>

        {registryTab ? (
          <FilterChips
            label={tab === 'rules' ? 'When' : 'Kind of action'}
            options={tab === 'rules' ? [...RULE_EVENTS] : [...ACTION_CATEGORIES]}
            value={tab === 'rules' ? event : category}
            labelOf={tab === 'rules' ? eventLabel : categoryLabel}
            onChange={(v) => set(tab === 'rules' ? { event: v } : { category: v })}
          />
        ) : null}

        <TabsContent value="actions" className="pt-2">
          {tab === 'actions' ? <ActionGrid q={q} category={category} origin={origin} onCreate={(d) => setCreating({ kind: 'action', description: d })} /> : null}
        </TabsContent>
        <TabsContent value="rules" className="pt-2">
          {tab === 'rules' ? <RuleGrid q={q} event={event} origin={origin} onCreate={(d) => setCreating({ kind: 'rule', description: d })} /> : null}
        </TabsContent>
        <TabsContent value="capabilities" className="pt-2">
          {tab === 'capabilities' ? <CapabilitiesEditor q={q} /> : null}
        </TabsContent>
      </Tabs>

      {registryTab ? <TypeLegend /> : null}

      <CreateWithAIDialog
        open={creating !== null}
        onOpenChange={(o) => (!o ? setCreating(null) : undefined)}
        initialKind={creating?.kind ?? kind}
        initialDescription={creating?.description ?? ''}
        pollMs={jobPollMs}
        stream={jobStream}
      />
    </div>
  )
}

function FilterChips({ label, options, value, labelOf, onChange }: { label: string; options: string[]; value: string | undefined; labelOf(v: string): string; onChange(v: string | undefined): void }) {
  const all = value && !options.includes(value) ? [...options, value] : options
  return (
    <div className="mt-3 flex flex-wrap items-center gap-1.5" role="group" aria-label={label}>
      <Chip size="sm" selected={!value} onClick={() => onChange(undefined)}>
        All
      </Chip>
      {all.map((o) => (
        <Chip key={o} size="sm" selected={value === o} onClick={() => onChange(value === o ? undefined : o)}>
          {labelOf(o)}
        </Chip>
      ))}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Grids                                                                       */
/* -------------------------------------------------------------------------- */

function ActionGrid({ q, category, origin, onCreate }: { q: string; category?: string; origin?: Origin; onCreate(description: string): void }) {
  const list = useQuery(actionsQuery({ q, category, origin }))
  const toggle = useToggleEnabled('action')
  // The server filters; the exact-match fields are re-checked here so a lenient server still shows the right cards.
  const items = (list.data ?? []).filter((a) => (!category || a.category === category) && (!origin || a.origin === origin))
  return (
    <Grid
      pending={list.isPending}
      error={list.isError}
      onRetry={() => void list.refetch()}
      empty={items.length === 0}
      emptyText={q ? `No actions match “${q}”.` : 'No actions here yet.'}
      onCreate={() => onCreate(q)}
      createLabel={q ? `Teach the studio “${q}”` : 'Create one with AI'}
      label="Actions"
    >
      {items.map((a) => (
        <RegistryCard
          key={a.id}
          kind="action"
          doc={a}
          title={a.label}
          roles={a.roles}
          tag={a.category ? categoryLabel(a.category) : null}
          busy={toggle.isPending && toggle.variables?.id === a.id}
          onToggle={(enabled) => toggle.mutate({ id: a.id, enabled })}
        />
      ))}
    </Grid>
  )
}

function RuleGrid({ q, event, origin, onCreate }: { q: string; event?: string; origin?: Origin; onCreate(description: string): void }) {
  const list = useQuery(rulesQuery({ q, event, origin }))
  const toggle = useToggleEnabled('rule')
  const items = (list.data ?? []).filter((r) => (!event || r.when?.event === event) && (!origin || r.origin === origin))
  return (
    <Grid
      pending={list.isPending}
      error={list.isError}
      onRetry={() => void list.refetch()}
      empty={items.length === 0}
      emptyText={q ? `No world rules match “${q}”.` : 'No world rules here yet.'}
      onCreate={() => onCreate(q)}
      createLabel={q ? `Teach the studio “${q}”` : 'Create one with AI'}
      label="World rules"
    >
      {items.map((r) => (
        <RegistryCard
          key={r.id}
          kind="rule"
          doc={r}
          title={ruleTitle(r)}
          roles={ruleRoles(r)}
          tag={r.when?.event ? `When: ${eventLabel(r.when.event).toLowerCase()}` : null}
          busy={toggle.isPending && toggle.variables?.id === r.id}
          onToggle={(enabled) => toggle.mutate({ id: r.id, enabled })}
        />
      ))}
    </Grid>
  )
}

/** A rule's two participants, as roles for the chips. */
export function ruleRoles(r: Pick<InteractionRule, 'when'>): Record<string, RoleSpec> {
  const out: Record<string, RoleSpec> = {}
  if (r.when?.subject) out.subject = r.when.subject
  if (r.when?.other) out.other = r.when.other
  return out
}

function Grid({
  pending,
  error,
  onRetry,
  empty,
  emptyText,
  onCreate,
  createLabel,
  label,
  children,
}: {
  pending: boolean
  error: boolean
  onRetry(): void
  empty: boolean
  emptyText: string
  onCreate(): void
  createLabel: string
  label: string
  children: ReactNode
}) {
  if (pending) {
    return (
      <ul className={GRID} aria-busy="true" aria-label={`Loading ${label.toLowerCase()}`}>
        {Array.from({ length: 6 }, (_, i) => (
          <li key={i} className="card flex h-40 flex-col gap-3 p-4">
            <div className="h-5 w-1/2 rounded bg-paper-2" />
            <div className="h-4 w-full rounded bg-paper-2" />
            <div className="mt-auto h-7 w-2/3 rounded-full bg-paper-2" />
          </li>
        ))}
      </ul>
    )
  }
  if (error) {
    return (
      <div className="card flex flex-col items-start gap-3 p-6" role="alert">
        <p className="text-ink">The {label.toLowerCase()} could not be loaded.</p>
        <Button variant="secondary" onClick={onRetry}>
          Try again
        </Button>
      </div>
    )
  }
  if (empty) {
    return (
      <div className="card flex flex-col items-center gap-3 p-8 text-center" data-testid="registry-empty">
        <p className="text-ink-2">{emptyText}</p>
        <Button variant="secondary" onClick={onCreate}>
          <Sparkles aria-hidden />
          {createLabel}
        </Button>
      </div>
    )
  }
  return (
    <ul className={GRID} role="list" aria-label={label} data-testid="registry-grid">
      {children}
    </ul>
  )
}

/* -------------------------------------------------------------------------- */
/* Card                                                                        */
/* -------------------------------------------------------------------------- */

interface RegistryCardProps {
  kind: RegistryKind
  doc: ActionDef | InteractionRule
  title: string
  roles: Record<string, RoleSpec>
  tag: string | null
  busy: boolean
  onToggle(enabled: boolean): void
}

/** Fixed slots: title + switch, one-line description, participant chips, origin + tag. */
function RegistryCard({ kind, doc, title, roles, tag, busy, onToggle }: RegistryCardProps) {
  return (
    <li
      className={cn('card relative flex min-w-0 flex-col gap-3 p-4 transition-shadow hover:shadow-pop focus-within:shadow-pop', !doc.enabled && 'opacity-75')}
      data-testid="registry-card"
      data-id={doc.id}
      data-enabled={doc.enabled}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-base font-semibold text-ink" title={title}>
            <Link to="/actions/$id" params={{ id: doc.id }} search={{ kind }} className="rounded-sm after:absolute after:inset-0 after:content-[''] focus-visible:focus-ring">
              {title}
            </Link>
          </h3>
          <p className="line-clamp-2 min-h-10 text-sm text-ink-2" title={doc.description || undefined}>
            {doc.description || ' '}
          </p>
        </div>
        <EnabledSwitch className="relative z-10 mt-0.5" checked={doc.enabled} onChange={onToggle} disabled={busy} label={`Use “${title}” in stories`} />
      </div>
      <RoleChips roles={roles} />
      <div className="mt-auto flex flex-wrap items-center gap-1.5">
        <OriginBadge origin={doc.origin} />
        {tag ? <Badge tone="neutral">{tag}</Badge> : null}
        {!doc.enabled ? <span className="text-xs text-ink-3">Off — stories won’t use it</span> : null}
      </div>
    </li>
  )
}
